import type { BaseTableSettings, TableSettingsState } from '@cpce/shared';
import type {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiPut } from '@/api/api.utils';
import { queryClient } from '@/api/queries.utils';
import {
  useTableSettings,
  useTableSettingsCommiteeId,
} from '@/api/table-settings/table-settings.queries';
import { tableSettingsQueryKeys } from '@/api/table-settings/table-settings.query-keys';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const SAVE_DEBOUNCE_MS = 600;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Everything a table needs to read and mutate its user-adjustable state
 * (column filters, sorting, search), plus the reset-to-defaults control.
 */
export interface TableStateController {
  tableId: string;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  search: string;
  setSearch: (search: string) => void;
  /** `search` trailing the input by a moment: what a server-side search sends, one request per pause. */
  debouncedSearch: string;
  /** False on pages whose search queries the server — the table must not re-filter rows itself. */
  searchInTable: boolean;
  isDirty: boolean;
  reset: () => void;
  /** False until the saved settings are known — the table renders its loading state meanwhile. */
  isLoaded: boolean;
}

interface UseTableStateOptions<TData> {
  columns: ColumnDef<TData, never>[] | ColumnDef<TData, unknown>[];
  initialSorting?: SortingState;
  /** Filters injected by the route (deep links) — they win over the saved filters and search for this visit. */
  urlColumnFilters?: ColumnFiltersState;
  /** Ephemeral tables (modals, wizard steps) pass false: reset works but nothing is stored. */
  persist?: boolean;
  /** Pass false when the page's search queries the server. */
  searchInTable?: boolean;
}

function getColumnId(column: ColumnDef<never, never>): string | undefined {
  if (column.id) return column.id;
  const accessorKey = (column as { accessorKey?: unknown }).accessorKey;
  // TanStack derives the same id from a dotted accessor key ('a.b' → 'a_b')
  if (typeof accessorKey === 'string') return accessorKey.replaceAll('.', '_');
  return undefined;
}

export function getDefaultColumnFilters(columns: readonly ColumnDef<never, never>[]) {
  return columns.flatMap((column) => {
    const defaultFilter = column.meta?.defaultFilter;
    const id = getColumnId(column);
    if (!id || defaultFilter === undefined || isEmptyFilterValue(defaultFilter)) return [];
    return [{ id, value: defaultFilter }];
  });
}

function isEmptyFilterValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && ('min' in value || 'max' in value)) {
    const range = value as { min?: unknown; max?: unknown };
    return range.min == null && range.max == null;
  }
  return false;
}

function canonicalFilterValue(value: unknown): unknown {
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    return value.map(canonicalFilterValue).sort((a, b) => String(a).localeCompare(String(b)));
  }
  if (value && typeof value === 'object' && ('min' in value || 'max' in value)) {
    const range = value as { min?: unknown; max?: unknown };
    return {
      min: canonicalFilterValue(range.min ?? null),
      max: canonicalFilterValue(range.max ?? null),
    };
  }
  return value ?? null;
}

/**
 * A filter entry can stay in the state with an empty value once cleared, values can be
 * Dates or their ISO round-trip, and entry/array order is meaningless — canonicalize
 * before comparing states.
 */
function normalizeColumnFilters(filters: ColumnFiltersState) {
  return filters
    .filter((filter) => !isEmptyFilterValue(filter.value))
    .map((filter) => ({ id: filter.id, value: canonicalFilterValue(filter.value) }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

// jsonb (the DB round-trip) reorders object keys, so JSON.stringify of raw entries is
// not a reliable equality — compare canonical projections instead.
function sortingKey(sorting: TableSettingsState['sorting']): string {
  return sorting.map((entry) => `${entry.id}:${entry.desc}`).join(',');
}

function statesEqual(a: TableSettingsState, b: TableSettingsState): boolean {
  return (
    a.search === b.search &&
    sortingKey(a.sorting) === sortingKey(b.sorting) &&
    JSON.stringify(normalizeColumnFilters(a.columnFilters)) ===
      JSON.stringify(normalizeColumnFilters(b.columnFilters))
  );
}

export function useTableState<TData>(
  tableId: string,
  options: UseTableStateOptions<TData>
): TableStateController {
  const {
    columns,
    initialSorting,
    urlColumnFilters,
    persist = true,
    searchInTable = true,
  } = options;

  const { data: settings, isLoading } = useTableSettings();
  const commiteeId = useTableSettingsCommiteeId() ?? null;
  const commiteeIdRef = useRef(commiteeId);
  commiteeIdRef.current = commiteeId;
  const saved = persist ? settings?.[tableId] : undefined;
  const isLoaded = !persist || !isLoading;

  const defaults = useMemo<TableSettingsState>(
    () => ({
      columnFilters: getDefaultColumnFilters(columns as ColumnDef<never, never>[]),
      sorting: initialSorting ?? [],
      search: '',
    }),
    [columns, initialSorting]
  );

  const [localFilters, setLocalFilters] = useState<ColumnFiltersState>();
  const [localSorting, setLocalSorting] = useState<SortingState>();
  const [localSearch, setLocalSearch] = useState<string>();

  // A page that stays mounted across a comité switch must not carry its edits over.
  const previousCommiteeIdRef = useRef(commiteeId);
  useEffect(() => {
    if (previousCommiteeIdRef.current === commiteeId) return;
    previousCommiteeIdRef.current = commiteeId;
    setLocalFilters(undefined);
    setLocalSorting(undefined);
    setLocalSearch(undefined);
  }, [commiteeId]);

  const hasUrlFilters = !!urlColumnFilters && urlColumnFilters.length > 0;
  const columnFilters =
    localFilters ??
    (hasUrlFilters ? urlColumnFilters : undefined) ??
    saved?.columnFilters ??
    defaults.columnFilters;
  const sorting = localSorting ?? saved?.sorting ?? defaults.sorting;
  const search =
    localSearch ??
    (hasUrlFilters ? defaults.search : undefined) ??
    saved?.search ??
    defaults.search;
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const currentStateRef = useRef<TableSettingsState>({ columnFilters, sorting, search });
  currentStateRef.current = { columnFilters, sorting, search };
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const flushSave = useCallback(() => {
    saveTimerRef.current = undefined;
    const state = currentStateRef.current;
    const commiteeId = commiteeIdRef.current;
    const queryKey = tableSettingsQueryKeys.forCommitee(commiteeId);
    // The cache holds the RAW row array (select() only transforms what consumers read)
    const rows = queryClient.getQueryData<BaseTableSettings[]>(queryKey);
    const cached = rows?.find((row) => row.tableId === tableId)?.state;
    if (cached ? statesEqual(state, cached) : statesEqual(state, defaultsRef.current)) return;
    queryClient.setQueryData<BaseTableSettings[]>(queryKey, (old) => [
      ...(old ?? []).filter((row) => row.tableId !== tableId),
      { tableId, state },
    ]);
    // Direct apiPut, not the mutation util: saving a preference must stay invisible —
    // no success toast on every keystroke, and a failed save only logs.
    apiPut(`/users/table-settings/${tableId}`, { state, commiteeId }).catch((error) => {
      // biome-ignore lint/suspicious/noConsole: intentionally silent for the user, but traceable
      console.error(`Failed to save table settings for "${tableId}"`, error);
    });
  }, [tableId]);

  const scheduleSave = useCallback(() => {
    if (!persist) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushSave, SAVE_DEBOUNCE_MS);
  }, [persist, flushSave]);

  useEffect(
    () => () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        flushSave();
      }
    },
    [flushSave]
  );

  const onColumnFiltersChange = useCallback(
    (updater: Updater<ColumnFiltersState>) => {
      const next =
        typeof updater === 'function' ? updater(currentStateRef.current.columnFilters) : updater;
      setLocalFilters(next);
      scheduleSave();
    },
    [scheduleSave]
  );

  const onSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const next =
        typeof updater === 'function' ? updater(currentStateRef.current.sorting) : updater;
      setLocalSorting(next);
      scheduleSave();
    },
    [scheduleSave]
  );

  const setSearch = useCallback(
    (value: string) => {
      setLocalSearch(value);
      scheduleSave();
    },
    [scheduleSave]
  );

  const reset = useCallback(() => {
    setLocalFilters(defaultsRef.current.columnFilters);
    setLocalSorting(defaultsRef.current.sorting);
    setLocalSearch('');
    scheduleSave();
  }, [scheduleSave]);

  const isDirty = useMemo(
    () => !statesEqual({ columnFilters, sorting, search }, defaults),
    [columnFilters, sorting, search, defaults]
  );

  return {
    tableId,
    columnFilters,
    onColumnFiltersChange,
    sorting,
    onSortingChange,
    search,
    setSearch,
    debouncedSearch,
    searchInTable,
    isDirty,
    reset,
    isLoaded,
  };
}
